import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as qs from 'qs';
import useSetIssuesScope from '../../issues/IssuesInbox/hooks/useSetIssuesScope';
import useCategorySummary from '../../issues/IssuesInbox/hooks/useCategorySummary';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { permittedIssueCategoriesToView } from '../../../helpers/issuesV2';
import { window } from '../../../globals';
import type { Category } from '../../../../shared/issuesV2';
import { useLocation } from '../../../contexts/RouterContext';

type Props = {
  baseRevisionURL: string;
  revisionScanId?: number;
  projectLocator: string;
  revisionId: string | null;
};

/**
 * Hook that returns props for the subnav item for project issues.
 * This mostly just interfaces with Redux, setting the scope and fetching
 * the issue category summary. In addition, it determines which subnav items
 * can actually be displayed, based on the users permissions.
 */
export default function useProjectIssuesSubnav({
  baseRevisionURL,
  revisionScanId,
  projectLocator,
  revisionId,
}: Props) {
  const location = useLocation();
  //  Ensure that the scope is correctly set, based on the params.
  //  Returns the scope so it can be passed to the other hook.
  const scope = useSetIssuesScope({
    project_id: projectLocator,
    revision_id: revisionId!,
    revisionScanId,
  });

  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const categorySummary = useCategorySummary(scope);
  const totalIssuesCount = useSelector(IssuesV2Store.selectors.getTotalNumberOfIssuesFromSummary);
  const getProjectIssuesURL = useCallback(
    (category?: Category): string => {
      const query = !revisionScanId ? '' : qs.stringify({ revisionScanId }, { addQueryPrefix: true }); // Adds the leading "?"
      return category
        ? `${baseRevisionURL}/issues/${category}${query}`
        : `${baseRevisionURL}/issues${query}`;
    },
    [baseRevisionURL, revisionScanId],
  );

  //  Recalculate the permitted categories only if user permissions actually change.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- This is fine.
  const permittedCategories = useMemo(() => permittedIssueCategoriesToView(), [window.user?.permissions]);

  const overviewPath = `${baseRevisionURL}/issues/overview`;
  const isActive = location.pathname.startsWith(`${baseRevisionURL}/issues`);
  const isOverview = location.pathname === overviewPath;

  // The count should be the current category if the issues page is active, otherwise the total.
  // The issue category from the store is always available, so that alone is not reliable.
  const issueCount = isActive && !isOverview ? categorySummary?.[issueCategory] : totalIssuesCount;

  return {
    issueCount,
    categorySummary,
    totalIssuesCount,
    permittedCategories,
    getProjectIssuesURL,
  };
}
